import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import DiscussionBoardService, {
  BlogPostBean,
  DiscussionBoardBean,
  extractErrorMessage,
} from "../../_services/DiscussionBoardService";
import "./discussion-board.css";

const initials = (name: string): string => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
};

const DiscussionBoardTopicsPage: React.FC = () => {
  const history = useHistory();
  const { boardId } = useParams<{ boardId: string }>();
  const [board, setBoard] = useState<DiscussionBoardBean | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    DiscussionBoardService.getBoard(boardId)
      .then((res) => setBoard(res.data))
      .catch((e) => setLoadError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [boardId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submitTopic = async () => {
    if (!draftTitle.trim() || !draftContent.trim()) return;
    setSubmitting(true);
    setAddError(null);
    try {
      await DiscussionBoardService.addTopic({
        title: draftTitle,
        content: draftContent,
        discussionBoardId: boardId,
      });
      setShowAdd(false);
      setDraftTitle("");
      setDraftContent("");
      reload();
    } catch (e: any) {
      setAddError(extractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const topics: BlogPostBean[] = useMemo(() => {
    if (!board) return [];
    const all = board.blogPostBeans || [];
    if (!filter) return all;
    const q = filter.toLowerCase();
    return all.filter((t) => t.title.toLowerCase().includes(q));
  }, [board, filter]);

  if (loading) {
    return (
      <div className="dbt-page">
        <div className="dbt-header" style={{ opacity: 0.7 }}>
          <div className="dbt-title-block">
            <div className="dbt-breadcrumb">Discussion Board</div>
            <h1 className="dbt-title">Loading…</h1>
          </div>
        </div>
        <div className="dbt-toolbar" />
        <div className="dbt-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="dbt-skeleton">
              <div className="dbt-skeleton-avatar" />
              <div style={{ flex: 1 }}>
                <div className="dbt-skeleton-line short" />
                <div className="dbt-skeleton-line shorter" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="dbt-page">
        <div className="dbt-error">
          <div className="dbt-error-icon">!</div>
          <h3 className="dbt-error-title">Couldn't load the discussion board</h3>
          <p className="dbt-error-text">{loadError}</p>
          <Button
            variant="primary"
            onClick={reload}
            style={{ marginTop: 18, borderRadius: 10, padding: "8px 20px", fontWeight: 500 }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!board) return null;

  const totalTopics = (board.blogPostBeans || []).length;
  const courseLabel = board.courseName || board.programName || "";

  return (
    <div className="dbt-page">
      {/* Header */}
      <div className="dbt-header">
        <Button className="dbt-back-btn" onClick={() => history.push("/discussion-board")}>
          ← Back
        </Button>
        <div className="dbt-title-block">
          <div className="dbt-breadcrumb">Discussion Board</div>
          <h1 className="dbt-title">{board.name}</h1>
          <div className="dbt-meta">
            {courseLabel && (
              <span className="dbt-meta-chip">📚 {courseLabel}</span>
            )}
            {totalTopics > 0 && (
              <span className="dbt-meta-chip">
                💬 {totalTopics} {totalTopics === 1 ? "topic" : "topics"}
              </span>
            )}
            {board.acceptStudentPosts && (
              <span className="dbt-meta-chip">🟢 Open for students</span>
            )}
            <span className="dbt-meta-chip">
              {board.sendEmails ? "📧 Email notifications on" : "🔕 Email notifications off"}
            </span>
          </div>
        </div>
        <Button className="dbt-new-btn" onClick={() => setShowAdd(true)}>
          + New Topic
        </Button>
      </div>

      {/* Toolbar */}
      <div className="dbt-toolbar">
        <div className="dbt-search">
          <span className="dbt-search-icon">🔍</span>
          <input
            type="text"
            className="dbt-search-input"
            placeholder="Search topics by title…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        {filter ? (
          <div className="dbt-count">
            <strong>{topics.length}</strong> of {totalTopics}
          </div>
        ) : totalTopics > 0 ? (
          <div className="dbt-count">
            <strong>{totalTopics}</strong> {totalTopics === 1 ? "topic" : "topics"}
          </div>
        ) : null}
      </div>

      {/* Topic list */}
      {topics.length === 0 ? (
        <div className="dbt-empty">
          <div className="dbt-empty-icon">💬</div>
          <h3 className="dbt-empty-title">
            {filter ? "No topics match your search" : "No topics yet"}
          </h3>
          <p className="dbt-empty-text">
            {filter
              ? "Try a different keyword, or clear the filter to see everything."
              : "Be the first to start a conversation in this board."}
          </p>
          {!filter && (
            <Button className="dbt-new-btn" onClick={() => setShowAdd(true)}>
              + Start the first topic
            </Button>
          )}
        </div>
      ) : (
        <div className="dbt-list">
          {topics.map((t: BlogPostBean) => {
            const pinned = Number(t.sticky) > 0;
            const author = (t.userName || "?").split(" - ")[0] || "?";
            return (
              <div
                key={t.id}
                className={`dbt-topic ${pinned ? "dbt-topic-pinned" : ""}`}
                onClick={() => history.push(`/discussion-board/${boardId}/topic/${t.id}`)}
              >
                <div className="dbt-topic-avatar">{initials(author)}</div>
                <div className="dbt-topic-body">
                  <h4 className="dbt-topic-title">
                    {t.title}
                    {pinned && <span className="dbt-pin-badge">📌 Pinned</span>}
                  </h4>
                  <div className="dbt-topic-meta">{t.postedon}</div>
                </div>
                <div className="dbt-topic-arrow">→</div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Topic Modal */}
      <Modal
        show={showAdd}
        onHide={() => {
          setShowAdd(false);
          setAddError(null);
        }}
        size="lg"
        centered
        dialogClassName="dbt-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Start a new topic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addError && (
            <div
              style={{
                background: "var(--status-danger-bg)",
                border: "1px solid var(--status-danger-border)",
                color: "var(--status-danger)",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: "0.88rem",
                wordBreak: "break-word",
              }}
            >
              {addError}
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="dbt-modal-label">Subject</Form.Label>
            <Form.Control
              type="text"
              className="dbt-modal-input"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="What's this topic about?"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="dbt-modal-label">Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              className="dbt-modal-textarea"
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              placeholder="Share the context, question, or discussion point…"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAdd(false);
              setAddError(null);
            }}
            disabled={submitting}
            style={{ borderRadius: 10, padding: "8px 18px", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitTopic}
            disabled={submitting || !draftTitle.trim() || !draftContent.trim()}
            style={{ borderRadius: 10, padding: "8px 22px", fontWeight: 500 }}
          >
            {submitting ? "Posting…" : "Post Topic"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DiscussionBoardTopicsPage;
